import { CompanyDetailsFormView } from '../view/CompanyDetailsFormView';
import { templateCompanyDetailsForm } from '../view/template';
import { BaseWebComponentController, WebComponentEvent } from '../../../../framework/BaseWebComponentController';
import { CompanyDetailsFormWebComponent } from '../component/CompanyDetailsFormWebComponent';
import { CompanyDetailsFormRepository } from '../repository/CompanyDetailsFormRepository';
import { CompanyDetailsConstants } from '../../companyDetailsTab/CompanyDetailsConstants';
import { CompanyDetailsFormViewData } from '../types/CompanyDetailsFormViewData';
import { CompanyType } from '../../../types/company-type';
import { CompanyActionContext } from '../../types/CompanyActionContext';
import { toggleVisibility } from '../../../../utils/htmlElement.utils';
import { CompanyDetailsEvents } from '../types/CompanyDetailsEvents';
import { CompanyData } from '../../../types/company-data';
import { SubscriptionType } from '../../../../license/types/SubscriptionType';
import { FormEvent } from '../../../../ui/gzForm/formField/types/form-event.enum';
import { InformativeDialog } from '../../uiElements/InformativeDialog';
import { GzDialogWebComponent } from '../../../../ui/gzDialog';
import { GzDialogEvents } from '../../../../ui/gzDialog/component/GzDialog';
import { InputEvents } from '../../../../ui/gzInput/types';
import { GzToastNotificationWebComponent } from '../../../../index.ui';
import { ToastSeverity } from '../../../../ui/gzToastNotification/type';
import { MdrContactDetailsFormEvents } from '../../mdrContactDetails/mdrContactDetailsForm/types';
import { ProductServiceType } from '../../../../license/types/ProductServiceType';
import { ProductServiceStatus } from '../../../../license/types/ProductServiceStatus';

const MAX_IMAGE_SIZE_ALLOWED = 1024 * 1024; // 1MB

export class CompanyDetailsFormController extends BaseWebComponentController {
  public component: CompanyDetailsFormWebComponent;
  public view: CompanyDetailsFormView;
  public viewConstructor: typeof CompanyDetailsFormView;
  public dataRepository: CompanyDetailsFormRepository;
  public dataRepositoryConstructor: typeof CompanyDetailsFormRepository;

  public disclaimer: string =
    'You understand and agree that You are providing the partner access to Your quarantined files, and You are directly responsible for the all the risks and liability from such download and from any further use of such file. Bitdefender does not accept any responsibility or liability for the content of the downloaded files and Bitdefender shall not be liable for any loss or damage of whatsoever nature (direct, indirect, consequential or other) whether arising in contract, tort or otherwise, which may arise as a result of the partner’s download or of Your use or the partner’s use of the downloaded file. Moreover, You are responsible for the partner using such files for other reasons whatsoever, especially with malicious intent, than for the purpose of further internal analysis of detected and quarantined malware file. You understand that the files may contain personal data and that Bitdefender will no longer be a joint controller with You for further purposes and additional processing by You or the partner on such data; You shall be an independent controller and the partner shall process such potential data for and on your behalf, with all the rights and obligations that such roles entail under the applicable data protection laws.';

  constructor(component: CompanyDetailsFormWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: CompanyDetailsFormView,
      viewTemplate: templateCompanyDetailsForm,
      dataRepositoryConstructor: CompanyDetailsFormRepository,
    });
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$companyNameFormField.Input,
        event: 'blur',
        callback: this.onCompanyNameBlur,
      },
      {
        element: this.view.dom.$countryFormField.Input,
        event: 'selectionChanged',
        callback: this.onCountryDropdownChange,
      },
      {
        element: this.view.dom.$contactRoleFormField.Input,
        event: 'selectionChanged',
        callback: this.onContactRoleDropdownChange,
      },
      {
        element: this.view.dom.$companyTypeFormField.Input,
        event: 'selectionChanged',
        callback: this.onCompanyTypeChange,
      },
      {
        element: this.view.dom.$canBeManagedByAboveFormField.Input,
        event: 'change',
        callback: this.onCanBeManagedByAboveChanged,
      },
      {
        element: this.view.dom.$allowDownloadQuarantineByAboveFormField.Input,
        event: InputEvents.CHANGE,
        callback: this.onAllowDownloadQuarantineByAboveChanged,
      },
      {
        element: this.view.dom.$logoFormField.Input,
        event: 'change',
        callback: this.onLogoInputChange,
      },
      {
        element: this.view.dom.$logoResetButton,
        event: 'click',
        callback: this.resetLogoToDefault,
      },
      {
        element: this.view.dom.$manageNetworksFormField,
        event: FormEvent.ValueChange,
        callback: this.onCanManageNetworksChange,
      },
      {
        element: this.view.dom.$mdrContactDetailsImportFromGeneralInfoButton,
        event: 'click',
        callback: this.importMdrContactDetailsFromGeneralInfo,
      },
      {
        element: this.view.dom.$mdrContactDetailsVerifyEmailButton,
        event: 'click',
        callback: this.verifyMdrContactEmail,
      },
      {
        element: this.view.dom.$mdrContactDetailsSectionYearly,
        event: MdrContactDetailsFormEvents.FORM_GROUP_CREATED,
        callback: this.onMdrContactDetailsYearlyFormGroupCreated,
      },
    ];
  }

  public async getDataForView(): Promise<CompanyDetailsFormViewData> {
    const countries = this.dataRepository.getCountries();
    const states = this.dataRepository.getStates();
    const companyRoles = this.dataRepository.getCompanyRoles();
    const industries = this.dataRepository.getIndustries();
    const defaultLogoId = this.dataRepository.getDefaultLogoId();
    const companyTypes = this.dataRepository.getCompanyTypes();
    const companyCustomizations = this.dataRepository.getCompanyCustomizations();
    const languages = this.dataRepository.getLanguages();
    const loggedUser = this.dataRepository.getLoggedUser();
    const rootVisibilityTree = this.dataRepository.getRootVisibilityTree();

    const promises: Promise<any>[] = [
      countries,
      states,
      companyRoles,
      industries,
      defaultLogoId,
      companyTypes,
      companyCustomizations,
      loggedUser,
      languages,
      rootVisibilityTree,
    ];

    if (this.component.formContext === CompanyActionContext.Create) {
      promises.push(this.dataRepository.getTimezones());
    }

    // make all request in parallel
    const viewData = await Promise.all(promises);

    // typescript will make sure the types are correct AOT
    return {
      countries: viewData[0],
      states: viewData[1],
      companyRoles: viewData[2],
      industries: viewData[3],
      defaultLogoId: viewData[4],
      companyTypes: viewData[5],
      companyCustomizations: viewData[6],
      loggedUser: viewData[7],
      languages: viewData[8],
      rootVisibilityTree: viewData[9],
      timezones: viewData[10],
      formContext: this.component.formContext,
    };
  }

  public async uploadLogo(): Promise<void> {
    // upload new logo if it was changed
    if (this.view.dom.$logoFormField.value == null || !this.view.dom.$logoFileIdFormField.hasUnsavedChanges) {
      return;
    }

    const logoFile = this.view.dom.$logoFormField.value[0];
    try {
      const response = await this.dependencies.api.clients.Company.UpdateLogo(logoFile).toPromise();

      // set the value in hidden form field
      this.view.dom.$logoFileIdFormField.value = response.fileId;
    } catch (error) {
      let msg: string;

      switch (error.exceptionType) {
        case 'Exceptions\\CompanyLogoUploadException':
          msg = this.dependencies.translateFn('Server or network error during upload. Please try again.');
          break;
        case 'Exceptions\\CompanyLogoInvalidImageException':
          msg = this.dependencies.translateFn('Invalid image file. Please upload a valid JPG or PNG image.');
          break;
        default:
          // should improve the error handling on the backend side
          if (logoFile.size > MAX_IMAGE_SIZE_ALLOWED) {
            msg = this.dependencies.translateFn('Image file is too large. Please upload an image of maximum 1 MB.');
          } else {
            msg = this.dependencies.translateFn('Server or network error during upload. Please try again.');
          }

          break;
      }

      throw new Error(msg);
    }
  }

  private onCompanyNameBlur() {
    const value = this.view.dom.$companyNameFormField.value;
    const trimmedValue = value.trim();

    if (value === trimmedValue) {
      return;
    }

    this.view.dom.$companyNameFormField.value = trimmedValue;
  }

  private async resetLogoToDefault(): Promise<void> {
    // reset user-selected logo, if any
    this.view.dom.$logoFormField.value = null;

    // set default logo in form
    await this.view.setLogoImg(this.view.defaultLogoId);
  }

  private onCanManageNetworksChange(): void {
    const canManageNetworks = this.view.dom.$manageNetworksFormField.value;

    this.view.updateManageNetworksToggleSection(true);

    this.component.dispatchEvent(
      new CustomEvent(CompanyDetailsEvents.ManageNetworkChange, {
        detail: {
          canManageNetworks: canManageNetworks,
        },
      })
    );
  }

  private onCountryDropdownChange(): void {
    const filteredStates = this.view.states.filter(state => {
      return state.countryCode === this.view.dom.$countryFormField.value;
    });

    if (filteredStates.length > 0) {
      this.view.dom.$stateFormField.classList.remove('hidden');

      this.view.dom.$stateFormField.Input.options = filteredStates.map(state => {
        return {
          title: state.stateName,
          value: state.stateCode,
        };
      });

      this.view.formGroup.addFormChild(['specifics', 'state'], this.view.dom.$stateFormField);
    } else {
      toggleVisibility(this.view.dom.$stateFormField, false);

      this.view.formGroup.removeFormChild(['specifics', 'state']);
    }

    this.view.dom.$stateFormField.value = '';
    this.view.dom.$stateFormField.Input.clearInput();
  }

  private onContactRoleDropdownChange() {
    const isOtherOptionSelected =
      this.view.dom.$contactRoleFormField.value === CompanyDetailsConstants.CompanyRoles.Other;

    // use the dropdown selected option by default, or the text-field input if the "Other" option is selected
    // add form child will replace the form field since it's the same path
    this.view.contactPersonFormGroup.addFormChild(
      ['companyRole'],
      isOtherOptionSelected ? this.view.dom.$contactRoleOtherFormField : this.view.dom.$contactRoleFormField
    );

    // show or hide the Company Role text-field input
    toggleVisibility(this.view.dom.$contactRoleOtherFormField, isOtherOptionSelected);
  }

  private onCompanyTypeChange() {
    const companyType = this.view.dom.$companyTypeFormField.value;
    const isCustomerSelected = companyType === CompanyType.Customer;
    const isPartnerSelected = companyType === CompanyType.Partner;

    // what the user selected in the Company Type dropdown (preselected or changed now)
    const isCustomerCompanyTypeSelected = this.view.dom.$companyTypeFormField.value === CompanyType.Customer;

    this.view.dom.$manageNetworksFormField.Input.readonly = isCustomerCompanyTypeSelected;

    if (isCustomerCompanyTypeSelected) {
      this.view.dom.$manageNetworksFormField.value = true;
    }

    // Update manage network toggle section when the field is updated for customer case
    // If this is called before, the warning is not shown accordingly
    // showWarnings should be true in order to update warning state
    this.view.updateManageNetworksToggleSection(true);

    if (this.view.editedCompanyData) {
      const existingCompanyType = CompanyData.getCompanyType(this.view.editedCompanyData);
      const isPartner = existingCompanyType === CompanyType.Partner;
      const isCustomer = existingCompanyType === CompanyType.Customer;
      const subscriptionType = CompanyData.determineCompanySubscription(this.view.editedCompanyData);

      const companyHasChildren = (this.view.editedCompanyData.children ?? 0) > 0;
      const isSwitchingToCustomer = isPartner && isCustomerSelected;
      const isSwitchingToPartner = isCustomer && isPartnerSelected;
      const canBeManagedByAbove = this.view.editedCompanyData.specifics.canBeManagedByAbove ?? false;
      const hasNoProducts =
        CompanyData.hasNonePaymentPlan(this.view.editedCompanyData) && subscriptionType === SubscriptionType.Licensed;
      const isMdrEnrolled = CompanyData.hasProductServiceWithStatus(
        this.view.editedCompanyData,
        ProductServiceType.MDR_MANAGER,
        [ProductServiceStatus.ONBOARDING, ProductServiceStatus.ACTIVE, ProductServiceStatus.DISABLING]
      );

      const canSwitchToCustomer = !companyHasChildren && !hasNoProducts;

      toggleVisibility(
        this.view.dom.$partnerHasToAddProductsToSwitchToCustomerNoticeContainer,
        isSwitchingToCustomer && hasNoProducts
      );

      toggleVisibility(
        this.view.dom.$partnerHasManagedNetworksNoticeContainer,
        isSwitchingToCustomer && companyHasChildren
      );

      toggleVisibility(
        this.view.dom.$partnerWillLoseResellAddonsToggleNoticeContainer,
        isSwitchingToCustomer && !companyHasChildren && subscriptionType === SubscriptionType.Inherited
      );

      toggleVisibility(
        this.view.dom.$partnerWillLoseMdrReportsAccessNoticeContainer,
        isSwitchingToCustomer && isMdrEnrolled
      );

      toggleVisibility(
        this.view.dom.$customerToPartnerResellingNoticeContainer,
        isSwitchingToPartner && !companyHasChildren && subscriptionType === SubscriptionType.Inherited
      );

      // Check if companyType is switched to Partner & disable the input if company has children or cannot be managed
      if (!isCustomerSelected) {
        this.view.dom.$manageNetworksFormField.Input.readonly = companyHasChildren || !canBeManagedByAbove;
      }

      this.view.setCompanyTypeValidator(!isPartner || (isSwitchingToCustomer && canSwitchToCustomer));

      this.view.setupMdrContactDetailsSection().then();
    } else {
      toggleVisibility(this.view.dom.$partnerWillLoseResellAddonsToggleNoticeContainer, false);
    }

    this.component.dispatchEvent(
      new CustomEvent(CompanyDetailsEvents.CompanyTypeChange, {
        detail: {
          companyType,
        },
        bubbles: true,
      })
    );
  }

  private onAllowDownloadQuarantineByAboveChanged(ev: WebComponentEvent): void {
    if (this.component.formContext === CompanyActionContext.MyCompany && ev.detail.checked && !this.view.isInit) {
      const dialog: GzDialogWebComponent = InformativeDialog.open(this.disclaimer);
      this.attachListeners(dialog, () => {
        this.view.dom.$allowDownloadQuarantineByAboveFormField.value = false;
        dialog.close();
      });
    }
    this.view.isInit = false;
  }

  private attachListeners(dialog: GzDialogWebComponent, cancelCallback: Function): void {
    dialog.addEventListener(GzDialogEvents.CANCEL, event => cancelCallback(dialog, event), { once: true });
    dialog.addEventListener(GzDialogEvents.CONFIRM, () => dialog.close(), { once: true });
  }

  private onCanBeManagedByAboveChanged() {
    if (this.component.formContext === CompanyActionContext.MyCompany) {
      this.view.updateRedactionCheckboxStateByManagedByAbove();
    }

    if (this.component.formContext !== CompanyActionContext.Create) {
      return;
    }

    this.view.updatePrimaryAccountRequiredness();

    this.component.dispatchEvent(
      new CustomEvent(CompanyDetailsEvents.ManageByAboveChange, {
        detail: {
          canBeManagedByAbove: this.view.dom.$canBeManagedByAboveFormField.value,
        },
        bubbles: false,
      })
    );
  }

  private async onLogoInputChange(): Promise<void> {
    if (!this.view.dom.$logoFormField.value?.length) {
      return;
    }

    const logoFile = this.view.dom.$logoFormField.value[0];

    // set logo in a <img> tag so users can see it
    const reader = new FileReader();
    reader.onload = () => {
      this.view.dom.$logoImg.src = (reader?.result || '').toString();
      this.view.dom.$logoFileIdFormField.value = logoFile.name; // temp id
    };

    reader.readAsDataURL(logoFile);

    this.view.dom.$logoResetButton.style.display = 'block';
  }

  private importMdrContactDetailsFromGeneralInfo() {
    this.view.importMdrContactDetailsFromGeneralInfo();
  }

  private async verifyMdrContactEmail() {
    if (!this.view.dom.$mdrContactDetailsEmailFormField.valid) {
      return;
    }

    this.view.dom.$mdrContactDetailsVerifyEmailButton.disabled = true;
    try {
      await this.dependencies.api.clients.EmailVerification.Generate(
        this.view.dom.$mdrContactDetailsEmailFormField.value,
        this.dependencies.Ext.Constants.EmailVerification.Purpose.REASON_MDR_CONTACT_DETAILS
      ).toPromise();
      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: this.dependencies.translateFn('Verification email sent.'),
        severity: ToastSeverity.SUCCESS,
      });
    } catch (error) {
      this.view.dom.$mdrContactDetailsVerifyEmailButton.disabled = false;
      const exceptionType = error?.exceptionType ?? null;
      let message: string;
      switch (exceptionType) {
        case 'Exceptions\\LimitExceededException':
          message = this.dependencies.translateFn('Email verification limit reached. Please try again later.');
          break;
        default:
          message = this.dependencies.translateFn('Could not send verification email');
          break;
      }

      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: message,
        severity: ToastSeverity.ERROR,
      });
    }
  }

  private onMdrContactDetailsYearlyFormGroupCreated(): void {
    this.view.addFormChildMdrContactInformationYearly();
    this.view.refreshInitialValues();
  }
}
